import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../../i18n/useTranslation';
import { Alert, type AlertTone } from '../atoms/Alert/Alert';
import { Badge } from '../atoms/Badge/Badge';
import { Button } from '../atoms/Button/Button';
import { Inline } from '../atoms/Inline/Inline';
import { Stack } from '../atoms/Stack/Stack';
import { Heading } from '../atoms/Typography/Heading';
import { Text } from '../atoms/Typography/Text';
import { LiveRegion } from '../atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../feedback/LoadErrorState/LoadErrorState';
import {
  REEF_GUARD_FINDINGS_KEY,
  acknowledgeReefGuardFinding,
  listReefGuardFindings,
  snoozeReefGuardFinding,
  type ReefGuardFinding,
} from '../../services/reefGuardService';
import {
  EMAIL_PREFERENCES_QUERY_KEY,
  fetchEmailPreferences,
  uiLocale,
  updateEmailPreferences,
  type EmailPreferences,
} from '../../services/emailPreferencesService';
import { createTask } from '../../services/taskService';
import './ReefGuardFindings.css';

const TONE: Record<ReefGuardFinding['severity'], AlertTone> = { critical: 'danger', warning: 'warning', hint: 'info' };
const UNIT: Readonly<Record<string, string>> = {
  alkalinity: 'dKH',
  salinity: 'ppt',
  calcium: 'ppm',
  magnesium: 'ppm',
  temperature: '°C',
  nitrate: 'ppm',
  phosphate: 'ppm',
};
const SNOOZE_HOURS = 24;
/**
 * Rules whose source is Reef Guard's own heuristic, not a citation: their stored
 * source text is internal ("… (UNVERIFIED; hobby consensus, editable)") and is
 * shown as a translated sentence instead (usability audit §3.6).
 */
const OWN_HEURISTIC_RULES: ReadonlySet<string> = new Set(['stale_test', 'salinity_drift', 'temperature_swing']);

function formatNumber(value: number, language: string): string {
  return new Intl.NumberFormat(language, { maximumFractionDigits: 2 }).format(value);
}

/** Local 'YYYY-MM-DD' of tomorrow (the re-test due date). */
function tomorrowDate(): string {
  const d = new Date(Date.now() + 24 * 3_600_000);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Re-test after a swing (#1823): one click creates a "re-test" task due
 * tomorrow; ignoring it is the dismissal.
 */
function RetestTaskButton({ finding, parameter }: { finding: ReefGuardFinding; parameter: string }) {
  const { t } = useTranslation();
  const create = useMutation({
    mutationFn: () =>
      createTask(finding.aquariumId, {
        title: String(t('reefGuard.retest.taskTitle', { parameter })),
        description: String(t('reefGuard.retest.taskDescription')),
        priority: finding.severity === 'critical' ? 'high' : 'medium',
        category: 'water_testing',
        dueDate: tomorrowDate(),
      }),
  });
  if (create.isSuccess) {
    return <LiveRegion><Text color="muted" variant="caption">{t('reefGuard.retest.created')}</Text></LiveRegion>;
  }
  return (
    <Stack gap="1">
      <Button size="sm" variant="secondary" onClick={() => create.mutate()} disabled={create.isPending}>
        {t('reefGuard.retest.create')}
      </Button>
      {create.isError && <Alert tone="danger" compact>{t('reefGuard.retest.failed')}</Alert>}
    </Stack>
  );
}

/** One finding: severity, what changed (values, time span), why it matters, what to do next, source. */
function FindingAlert({ finding, onAcknowledge, onSnooze, busy }: {
  finding: ReefGuardFinding;
  onAcknowledge: (id: string) => void;
  onSnooze: (id: string) => void;
  busy: boolean;
}) {
  const { t, i18n } = useTranslation();
  const fmt = (n: number) => formatNumber(n, i18n.language);
  const unit = UNIT[finding.parameterId] ?? '';
  const base = `reefGuard.rules.${finding.ruleId}`;
  const stale = finding.ruleId === 'stale_test';
  const parameter = String(t(`measurement.history.parameterNames.${finding.parameterId}`));
  const values = {
    from: fmt(finding.details.from),
    to: fmt(finding.details.to),
    delta: fmt(Math.abs(finding.details.delta)),
    perDay: fmt(Math.abs(finding.details.perDay)),
    hours: fmt(finding.details.hours),
    unit,
    parameter,
    // Stale test (#1823): hours = since the last test, threshold = interval hours.
    days: Math.floor(finding.details.hours / 24),
    interval: fmt(finding.details.threshold / 24),
    // Nutrient levels (#1827): the early-warning level.
    threshold: fmt(finding.details.threshold),
  };

  return (
    <li className="reef-guard-findings__item" data-testid={`reef-guard-finding-${finding.id}`}>
      <Alert
        tone={TONE[finding.severity]}
        live="off"
        titleLevel={3}
        title={String(t(`${base}.title`, { aquarium: finding.aquariumName, parameter }))}
        action={
          <Inline gap="2">
            {!finding.acknowledged && (
              <Button size="sm" variant="secondary" onClick={() => onAcknowledge(finding.id)} disabled={busy}>
                {t('reefGuard.acknowledge')}
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => onSnooze(finding.id)} disabled={busy}>
              {t('reefGuard.snooze', { hours: SNOOZE_HOURS })}
            </Button>
          </Inline>
        }
      >
        <Stack gap="2">
          <Inline gap="2">
            <Badge tone={TONE[finding.severity] === 'danger' ? 'danger' : TONE[finding.severity]} data-testid="reef-guard-severity">
              {t(`reefGuard.severity.${finding.severity}`)}
            </Badge>
            {finding.acknowledged && <Badge tone="neutral">{t('reefGuard.acknowledged')}</Badge>}
          </Inline>
          <Text>{String(t(`${base}.${finding.variant}.why`, values))}</Text>
          <Text>
            <strong>{t('reefGuard.nextStep')}</strong> {String(t(`${base}.${finding.variant}.next`, values))}
          </Text>
          {stale ? (
            <Inline gap="4">
              <Link className="reef-guard-findings__link" to={`/aquariums/${finding.aquariumId}/measurements/session`}>
                {t('reefGuard.staleTest.addMeasurement')}
              </Link>
              <Link className="reef-guard-findings__link" to={`/aquariums/${finding.aquariumId}/parameter-targets#test-intervals`}>
                {t('reefGuard.staleTest.changeInterval')}
              </Link>
            </Inline>
          ) : (
            finding.severity !== 'hint' && <RetestTaskButton finding={finding} parameter={parameter} />
          )}
          {finding.ruleId === 'nutrient_ulns' && (
            <Inline gap="4">
              <Link className="reef-guard-findings__link" to={`/aquariums/${finding.aquariumId}/incidents`}>
                {t('reefGuard.nutrients.incidents')}
              </Link>
              <Link className="reef-guard-findings__link" to={`/aquariums/${finding.aquariumId}/comparisons`}>
                {t('reefGuard.nutrients.comparisons')}
              </Link>
            </Inline>
          )}
          <Text color="muted" variant="caption">
            {t('reefGuard.sourceLine', {
              source: OWN_HEURISTIC_RULES.has(finding.ruleId) ? t(`reefGuard.heuristicSource.${finding.ruleId}`) : finding.source,
              confidence: t(`reefGuard.confidence.${finding.confidence}`),
            })}
          </Text>
        </Stack>
      </Alert>
    </li>
  );
}

/**
 * One-time question (#1821): critical tank alerts by e-mail? Shown while the
 * user has not decided (criticalAlerts null) and the server can send e-mail.
 * Changeable later in Settings → E-Mail-Benachrichtigungen.
 */
function CriticalAlertConsentPrompt() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const prefs = useQuery({ queryKey: EMAIL_PREFERENCES_QUERY_KEY, queryFn: fetchEmailPreferences, retry: false });
  const decide = useMutation({
    mutationFn: (value: boolean) => updateEmailPreferences({ criticalAlerts: value, locale: uiLocale(i18n.language) }),
    onSuccess: (updated) => queryClient.setQueryData<EmailPreferences>(EMAIL_PREFERENCES_QUERY_KEY, updated),
  });
  const data = prefs.data;
  if (!data || !data.emailDeliveryAvailable || data.criticalAlerts !== null) return null;
  return (
    <Alert
      tone="info"
      live="off"
      titleLevel={3}
      title={t('reefGuard.consent.title')}
      data-testid="reef-guard-consent"
      action={
        <Inline gap="2">
          <Button size="sm" variant="primary" onClick={() => decide.mutate(true)} disabled={decide.isPending}>
            {t('reefGuard.consent.yes')}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => decide.mutate(false)} disabled={decide.isPending}>
            {t('reefGuard.consent.no')}
          </Button>
        </Inline>
      }
    >
      <Text>{t('reefGuard.consent.body')}</Text>
      {decide.isError && <Text>{t('reefGuard.consent.error')}</Text>}
    </Alert>
  );
}

/**
 * Reef Guard section of the Alerts page (#1820): trend / rate-of-change findings
 * over the measurement history of all the user's aquariums.
 */
export function ReefGuardFindings() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: REEF_GUARD_FINDINGS_KEY, queryFn: listReefGuardFindings });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: REEF_GUARD_FINDINGS_KEY });
  const acknowledge = useMutation({ mutationFn: acknowledgeReefGuardFinding, onSuccess: refresh });
  const snooze = useMutation({ mutationFn: (id: string) => snoozeReefGuardFinding(id, SNOOZE_HOURS), onSuccess: refresh });
  const busy = acknowledge.isPending || snooze.isPending;

  let body;
  if (query.isPending) {
    body = <LiveRegion busy><Text color="muted">{t('reefGuard.loading')}</Text></LiveRegion>;
  } else if (query.isError) {
    body = (
      <LoadErrorState
        title={t('reefGuard.loadError')}
        titleLevel={3}
        onRetry={() => void query.refetch()}
        retryLabel={t('alerts.retry')}
      />
    );
  } else if (query.data.length === 0) {
    body = <Text color="muted" data-testid="reef-guard-empty">{t('reefGuard.empty')}</Text>;
  } else {
    body = (
      <ul className="reef-guard-findings__list" data-testid="reef-guard-findings">
        {query.data.map((finding) => (
          <FindingAlert
            key={finding.id}
            finding={finding}
            onAcknowledge={(id) => acknowledge.mutate(id)}
            onSnooze={(id) => snooze.mutate(id)}
            busy={busy}
          />
        ))}
      </ul>
    );
  }

  return (
    <section className="reef-guard-findings" aria-labelledby="reef-guard-heading">
      <Stack gap="3">
        <Heading level={2} id="reef-guard-heading">{t('reefGuard.title')}</Heading>
        <Text color="muted">{t('reefGuard.subtitle')}</Text>
        <CriticalAlertConsentPrompt />
        {body}
      </Stack>
    </section>
  );
}
